import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, RefreshCw, Search as SearchIcon, PowerOff, Power, ArrowUp, ArrowDown, PhoneCall, MessageCircle } from "lucide-react";

const EMPTY = {
  key: "", label: "", context: "",
  weekly_calls: 40, avg_value: 220, miss_pct: 25,
  discover_terms: "",
  sample_call: [
    { s: "caller", t: "" },
    { s: "ai",     t: "" },
  ],
};

export default function IndustriesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // record being edited, or "new"
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("details");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/industries");
      setRows(data.industries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      (r.label || "").toLowerCase().includes(q) ||
      (r.key || "").toLowerCase().includes(q) ||
      (r.discover_terms || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [rows, query]);

  const startNew = () => {
    setForm(EMPTY);
    setTab("details");
    setEditing("new");
  };
  const startEdit = (row) => {
    setForm({
      key: row.key || "",
      label: row.label || "",
      context: row.context || "",
      weekly_calls: row.roi_defaults?.weekly_calls ?? 40,
      avg_value:    row.roi_defaults?.avg_value    ?? 220,
      miss_pct:     row.roi_defaults?.miss_pct     ?? 25,
      discover_terms: (row.discover_terms || []).join(", "),
      sample_call: Array.isArray(row.sample_call) && row.sample_call.length
        ? row.sample_call.map((t) => ({ s: t.s || "caller", t: t.t || "" }))
        : [{ s: "caller", t: "" }, { s: "ai", t: "" }],
    });
    setTab("details");
    setEditing(row);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setTab("details"); };

  const updateTurn = (idx, patch) => {
    setForm((f) => ({
      ...f,
      sample_call: f.sample_call.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };
  const addTurn = () => {
    setForm((f) => {
      const lastSpeaker = f.sample_call.length ? f.sample_call[f.sample_call.length - 1].s : "caller";
      return { ...f, sample_call: [...f.sample_call, { s: lastSpeaker === "ai" ? "caller" : "ai", t: "" }] };
    });
  };
  const removeTurn = (idx) => {
    setForm((f) => ({ ...f, sample_call: f.sample_call.filter((_, i) => i !== idx) }));
  };
  const moveTurn = (idx, dir) => {
    setForm((f) => {
      const next = [...f.sample_call];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return f;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...f, sample_call: next };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.label.trim()) return toast.error("Label is required.");
    const terms = (form.discover_terms || "")
      .split(",").map((t) => t.trim()).filter(Boolean);
    const cleanCall = (form.sample_call || [])
      .map((t) => ({ s: (t.s === "ai" ? "ai" : "caller"), t: (t.t || "").trim() }))
      .filter((t) => t.t);
    const payload = {
      label:          form.label.trim(),
      context:        form.context,
      weekly_calls:   Number(form.weekly_calls) || 0,
      avg_value:      Number(form.avg_value)    || 0,
      miss_pct:       Number(form.miss_pct)     || 0,
      discover_terms: terms.length ? terms : [form.label.trim()],
      sample_call:    cleanCall,
    };
    if (editing === "new" && form.key.trim()) payload.key = form.key.trim();

    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/industries", payload);
        toast.success("Industry created.");
      } else {
        await api.patch(`/admin/industries/${editing.id}`, payload);
        toast.success("Industry updated.");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/industries/${row.id}`, { active: !row.active });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/industries/${row.id}`);
      toast.success("Industry deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reseed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/industries/reseed");
      toast.success(`Re-seed complete · ${data.inserted} new record(s).`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-10 max-w-6xl" data-testid="industries-admin">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2">Back office · Taxonomy</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Industries</h1>
          <p className="text-neutral-500 max-w-2xl text-sm">
            Single source of truth for every industry. Drives the lead-discovery dropdown,
            the client industry picker, and the ROI defaults on <code>/for/:industry</code>.
            Add a new vertical here and it lights up everywhere.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reseed} disabled={busy} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="industries-reseed">
            <RefreshCw className="w-4 h-4" /> Re-seed defaults
          </button>
          <button onClick={startNew} className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="industries-new-btn">
            <Plus className="w-4 h-4" /> Add industry
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 relative max-w-md">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          data-testid="industries-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by label, key, or discover term…"
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 bg-white focus:outline-none focus:border-black text-sm"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-neutral-500 text-sm">Loading industries…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center bg-white" data-testid="industries-empty">
          <div className="font-display text-2xl mb-2">No industries match.</div>
          <p className="text-neutral-500 mb-6">Try clearing the search or seeding the defaults.</p>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
              <tr>
                <th className="text-left px-4 py-3">Label</th>
                <th className="text-left px-4 py-3">Key</th>
                <th className="text-left px-4 py-3">Discover terms</th>
                <th className="text-right px-4 py-3">Calls / wk</th>
                <th className="text-right px-4 py-3">Avg $</th>
                <th className="text-right px-4 py-3">Miss %</th>
                <th className="text-center px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`industry-row-${row.key}`}>
                  <td className="px-4 py-3 font-medium">{row.label}</td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-500">{row.key}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500 max-w-xs truncate" title={(row.discover_terms || []).join(", ")}>
                    {(row.discover_terms || []).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{row.roi_defaults?.weekly_calls ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">${row.roi_defaults?.avg_value ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{row.roi_defaults?.miss_pct ?? "—"}%</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 border ${row.active ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-neutral-100 border-neutral-300 text-neutral-500"}`}>
                      {row.active ? "on" : "off"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button onClick={() => toggleActive(row)} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1" data-testid={`industry-toggle-${row.key}`}>
                        {row.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => startEdit(row)} className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1" data-testid={`industry-edit-${row.key}`}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => remove(row)} className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-1" data-testid={`industry-delete-${row.key}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / create drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={cancel} data-testid="industries-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-2xl border border-neutral-900 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-amber-300">
                  {editing === "new" ? "New industry" : `Editing · ${editing.label}`}
                </div>
                <div className="text-xl font-bold mt-1">
                  {editing === "new" ? "Add a new vertical" : editing.label}
                </div>
              </div>
              <button type="button" onClick={cancel} className="text-neutral-400 hover:text-white" data-testid="industries-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-neutral-200 bg-neutral-50">
              <TabBtn active={tab === "details"}     onClick={() => setTab("details")}     testId="industries-tab-details">Details</TabBtn>
              <TabBtn active={tab === "sample_call"} onClick={() => setTab("sample_call")} testId="industries-tab-sample" icon={PhoneCall}>Sample call ({(form.sample_call || []).filter(t => t.t).length})</TabBtn>
            </div>

            {tab === "details" && (
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Label *" testId="ind-field-label">
                    <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="lf-input" />
                  </Field>
                  <Field label={editing === "new" ? "Key (auto if blank)" : "Key (locked)"} testId="ind-field-key">
                    <input
                      disabled={editing !== "new"}
                      value={editing === "new" ? form.key : (editing.key || "")}
                      onChange={(e) => setForm({ ...form, key: e.target.value })}
                      className="lf-input font-mono text-xs disabled:bg-neutral-100 disabled:text-neutral-500"
                      placeholder="e.g. dog_grooming"
                    />
                  </Field>
                </div>

                <Field label="Discover terms (comma-separated — used by lead discovery search)" testId="ind-field-terms">
                  <input
                    value={form.discover_terms}
                    onChange={(e) => setForm({ ...form, discover_terms: e.target.value })}
                    className="lf-input"
                    placeholder="e.g. Plumber, Emergency Plumber"
                  />
                </Field>

                <Field label="ROI calculator defaults · shown on /for/:industry" testId="ind-field-roi">
                  <div className="grid grid-cols-3 gap-3">
                    <SubField label="Weekly calls">
                      <input type="number" min={1} value={form.weekly_calls} onChange={(e) => setForm({ ...form, weekly_calls: e.target.value })} className="lf-input" />
                    </SubField>
                    <SubField label="Avg $ / job">
                      <input type="number" min={1} value={form.avg_value} onChange={(e) => setForm({ ...form, avg_value: e.target.value })} className="lf-input" />
                    </SubField>
                    <SubField label="Miss %">
                      <input type="number" min={0} max={100} value={form.miss_pct} onChange={(e) => setForm({ ...form, miss_pct: e.target.value })} className="lf-input" />
                    </SubField>
                  </div>
                </Field>

                <Field label="Industry context · injected into AI concierge system prompt" testId="ind-field-context">
                  <textarea
                    rows={6}
                    value={form.context}
                    onChange={(e) => setForm({ ...form, context: e.target.value })}
                    className="lf-input font-mono text-xs"
                    placeholder="INDUSTRY: PLUMBING\nTypical calls: emergencies, quote requests…"
                  />
                </Field>
              </div>
            )}

            {tab === "sample_call" && (
              <div className="px-6 py-5 space-y-4" data-testid="industries-sample-editor">
                <div className="bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <MessageCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Drives the animated phone-call loop on every <code>/for/{form.key || "industry"}</code> hero. Each turn types out with a live waveform. Aim for 4–6 short turns.
                  </div>
                </div>

                <div className="space-y-3">
                  {(form.sample_call || []).map((turn, i) => (
                    <div key={i} className="border border-neutral-200 p-3 bg-neutral-50" data-testid={`sample-turn-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <select
                          value={turn.s}
                          onChange={(e) => updateTurn(i, { s: e.target.value })}
                          className="lf-input py-1 px-2 text-xs font-mono uppercase w-32"
                          data-testid={`sample-speaker-${i}`}
                        >
                          <option value="caller">Caller</option>
                          <option value="ai">AI receptionist</option>
                        </select>
                        <span className="text-xs font-mono text-neutral-400">Turn {i + 1}</span>
                        <div className="ml-auto flex gap-1">
                          <IconBtn onClick={() => moveTurn(i, -1)} disabled={i === 0} title="Move up" testId={`sample-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></IconBtn>
                          <IconBtn onClick={() => moveTurn(i, +1)} disabled={i === form.sample_call.length - 1} title="Move down" testId={`sample-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></IconBtn>
                          <IconBtn onClick={() => removeTurn(i)} title="Remove" danger testId={`sample-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                        </div>
                      </div>
                      <textarea
                        rows={2}
                        value={turn.t}
                        onChange={(e) => updateTurn(i, { t: e.target.value })}
                        placeholder={turn.s === "ai" ? "e.g. Booked for 2pm. Confirmation texted to your phone." : "e.g. My AC just stopped working."}
                        className="lf-input text-sm"
                        data-testid={`sample-text-${i}`}
                      />
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addTurn}
                  className="lf-btn-outline text-xs inline-flex items-center gap-1"
                  data-testid="sample-add-turn"
                >
                  <Plus className="w-3.5 h-3.5" /> Add turn
                </button>

                {/* Live preview strip */}
                {(form.sample_call || []).some(t => t.t) && (
                  <div className="border border-neutral-200 bg-neutral-900 text-neutral-100 p-4 mt-4" data-testid="sample-preview">
                    <div className="text-xs font-mono uppercase tracking-widest text-amber-300 mb-3">Preview · what visitors will see</div>
                    <div className="space-y-2">
                      {(form.sample_call || []).filter(t => t.t).map((turn, i) => (
                        <div key={i} className={`flex ${turn.s === "ai" ? "" : "justify-end"}`}>
                          <div className={`text-xs px-2.5 py-1.5 border max-w-[85%] ${turn.s === "ai" ? "bg-orange-900/40 border-orange-700 text-orange-100" : "bg-neutral-800 border-neutral-700"}`}>
                            <div className="text-[9px] font-mono uppercase tracking-widest opacity-70 mb-0.5">{turn.s === "ai" ? "AI" : "Caller"}</div>
                            {turn.t}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-2">
              <button type="button" onClick={cancel} className="lf-btn-outline text-sm" data-testid="industries-cancel-btn">Cancel</button>
              <button type="submit" disabled={busy} className="lf-btn-primary text-sm" data-testid="industries-save-btn">
                {busy ? "Saving…" : editing === "new" ? "Create industry" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-medium border-b-2 transition inline-flex items-center gap-2 ${active ? "border-coral text-neutral-900 bg-white" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}
    >
      {Icon && <Icon className="w-4 h-4" />} {children}
    </button>
  );
}

function IconBtn({ onClick, children, disabled, title, testId, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`p-1.5 border ${danger ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-neutral-200 text-neutral-500 hover:bg-neutral-100"} disabled:opacity-30 disabled:cursor-not-allowed transition`}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
function SubField({ label, children }) {
  return (
    <div>
      <div className="text-[11px] text-neutral-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
